/* ==========================================================================
   Mondo London - adaptive page scroll handle
   A single, non-interactive line at the browser edge. It tracks page progress
   and switches between ink and paper from the local surface beneath it.
   ========================================================================== */
(function () {
  'use strict';
  var coarsePointer = window.matchMedia && window.matchMedia('(hover: none) and (pointer: coarse)').matches;
  if (coarsePointer) return;
  if (window.__mondoScrollHandle || !document.body) return;
  window.__mondoScrollHandle = true;

  var root = document.documentElement;
  var scroller = document.scrollingElement || root;
  var handle = document.createElement('span');
  handle.className = 'page-scroll-handle';
  handle.setAttribute('aria-hidden', 'true');
  document.body.appendChild(handle);

  var maxScroll = 0;
  var viewportHeight = 0;
  var handleHeight = 0;
  var handleTravel = 0;
  var active = false;
  var suspended = false;
  var paintFrame = null;
  var measureFrame = null;
  var samples = {};
  var forcedColors = window.matchMedia ? window.matchMedia('(forced-colors: active)') : null;
  var mobileMenu = document.querySelector('[data-mobile]');

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function pageHeight() {
    var body = document.body;
    return Math.max(
      scroller.scrollHeight || 0,
      root.scrollHeight || 0,
      body.scrollHeight || 0,
      root.offsetHeight || 0,
      body.offsetHeight || 0
    );
  }

  function currentScroll() {
    return clamp(scroller.scrollTop || window.pageYOffset || 0, 0, maxScroll);
  }

  function colourLuminance(value) {
    if (!value || value === 'transparent') return null;
    var numbers = value.match(/[\d.]+/g);
    if (!numbers || numbers.length < 3) return null;
    var alpha = numbers.length > 3 ? parseFloat(numbers[3]) : 1;
    if (!isFinite(alpha) || alpha <= 0.04) return null;
    return {
      alpha: alpha,
      value: (0.2126 * parseFloat(numbers[0]) + 0.7152 * parseFloat(numbers[1]) + 0.0722 * parseFloat(numbers[2])) / 255
    };
  }

  function thumbFor(image) {
    var src = image.currentSrc || image.src;
    if (!src) return null;
    var entry = samples[src];
    if (entry) return entry === 'loading' || entry === 'blocked' ? null : entry;

    samples[src] = 'loading';
    var probe = new Image();
    probe.crossOrigin = 'anonymous';
    try {
      var url = new URL(src, location.href);
      url.searchParams.set('width', '32');
      probe.src = url.toString();
    } catch (error) {
      probe.src = src;
    }
    probe.onload = function () {
      try {
        var width = 32;
        var height = Math.max(2, Math.round(width * probe.naturalHeight / probe.naturalWidth));
        var canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        var context = canvas.getContext('2d', { willReadFrequently: true });
        context.drawImage(probe, 0, 0, width, height);
        context.getImageData(0, 0, 1, 1);
        samples[src] = { context: context, width: width, height: height };
      } catch (error) {
        samples[src] = 'blocked';
      }
      requestPaint();
    };
    probe.onerror = function () {
      samples[src] = 'blocked';
      requestPaint();
    };
    return null;
  }

  function imageLuminanceAt(image, clientX, clientY) {
    var sample = thumbFor(image);
    if (!sample) return null;
    var rect = image.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;

    var rx = (clientX - rect.left) / rect.width;
    var ry = (clientY - rect.top) / rect.height;
    var naturalWidth = image.naturalWidth || sample.width;
    var naturalHeight = image.naturalHeight || sample.height;
    var scale = Math.max(rect.width / naturalWidth, rect.height / naturalHeight);
    var visibleWidth = rect.width / scale;
    var visibleHeight = rect.height / scale;
    var fx = ((naturalWidth - visibleWidth) / 2 + rx * visibleWidth) / naturalWidth;
    var fy = ((naturalHeight - visibleHeight) / 2 + ry * visibleHeight) / naturalHeight;
    var px = Math.min(sample.width - 2, Math.max(1, Math.round(fx * sample.width)));
    var py = Math.min(sample.height - 2, Math.max(1, Math.round(fy * sample.height)));

    try {
      var data = sample.context.getImageData(px - 1, py - 1, 3, 3).data;
      var luminance = 0;
      for (var i = 0; i < data.length; i += 4) {
        luminance += 0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2];
      }
      return luminance / (data.length / 4) / 255;
    } catch (error) {
      return null;
    }
  }

  function surfaceLuminanceAt(clientX, clientY) {
    var stack = document.elementsFromPoint
      ? document.elementsFromPoint(clientX, clientY)
      : [document.elementFromPoint(clientX, clientY)];
    var translucent = null;

    for (var i = 0; i < stack.length; i += 1) {
      var element = stack[i];
      if (!element || element === handle) continue;
      if (element.closest && element.closest('.page-scroll-handle,.cursor-dot')) continue;

      var style = window.getComputedStyle ? window.getComputedStyle(element) : null;
      if (style && (style.display === 'none' || style.visibility === 'hidden' || parseFloat(style.opacity) <= 0.04)) continue;

      if (element.tagName === 'IMG') {
        var imageLuminance = imageLuminanceAt(element, clientX, clientY);
        if (imageLuminance !== null) return imageLuminance;
        var imageSrc = element.currentSrc || element.src;
        return samples[imageSrc] === 'blocked' ? 0 : null;
      }

      var colour = colourLuminance(style && style.backgroundColor);
      if (!colour) continue;
      if (colour.alpha >= 0.95) return colour.value;
      if (translucent === null) translucent = colour.value;
    }
    return translucent === null ? 1 : translucent;
  }

  function readSurface(y) {
    var width = root.clientWidth || window.innerWidth || 1;
    var sampleX = Math.max(0, width - 1);
    var sampleY = clamp(y + handleHeight / 2, 0, Math.max(0, viewportHeight - 1));
    var luminance = surfaceLuminanceAt(sampleX, sampleY);
    if (luminance === null) {
      handle.classList.remove('has-surface-tone');
      handle.classList.remove('is-over-light');
      return;
    }
    handle.classList.add('has-surface-tone');
    handle.classList.toggle('is-over-light', luminance >= 0.5);
  }

  function paint() {
    paintFrame = null;
    if (!active || suspended) return;
    var progress = maxScroll > 0 ? currentScroll() / maxScroll : 0;
    var y = clamp(progress * handleTravel, 0, handleTravel);
    handle.style.transform = 'translate3d(0,' + y.toFixed(2) + 'px,0)';
    readSurface(y);
  }

  function requestPaint() {
    if (paintFrame === null) paintFrame = requestAnimationFrame(paint);
  }

  function measure() {
    measureFrame = null;
    viewportHeight = Math.max(
      1,
      window.visualViewport && window.visualViewport.height || 0,
      window.innerHeight || 0,
      root.clientHeight || 0
    );
    var scrollViewport = Math.max(1, scroller.clientHeight || root.clientHeight || viewportHeight);
    var documentHeight = Math.max(pageHeight(), scrollViewport);
    maxScroll = Math.max(0, documentHeight - scrollViewport);

    var naturalHeight = viewportHeight * scrollViewport / documentHeight;
    var coarse = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
    var minimumHeight = coarse ? 88 : 96;
    handleHeight = Math.min(viewportHeight, Math.max(naturalHeight, minimumHeight));
    handleTravel = Math.max(0, viewportHeight - handleHeight);
    handle.style.height = handleHeight.toFixed(2) + 'px';

    active = maxScroll > 1;
    handle.classList.toggle('is-active', active && !suspended);
    paint();
  }

  function requestMeasure() {
    if (measureFrame === null) measureFrame = requestAnimationFrame(measure);
  }

  document.addEventListener('scroll', requestPaint, { passive: true });
  window.addEventListener('resize', requestMeasure, { passive: true });
  window.addEventListener('pageshow', requestMeasure);
  window.addEventListener('load', requestMeasure, { once: true });
  document.addEventListener('load', function (event) {
    if (event.target && event.target.tagName === 'IMG') requestMeasure();
  }, true);
  document.addEventListener('shopify:section:load', requestMeasure);
  document.addEventListener('shopify:section:unload', requestMeasure);
  document.addEventListener('shopify:section:reorder', requestMeasure);

  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', requestMeasure, { passive: true });
  }
  if (window.ResizeObserver) {
    var resizeObserver = new ResizeObserver(requestMeasure);
    resizeObserver.observe(root);
    resizeObserver.observe(document.body);
  }
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(requestMeasure).catch(function () {});
  }

  function syncMobileMenu() {
    suspended = !!(mobileMenu && !mobileMenu.hidden);
    handle.classList.toggle('is-suspended', suspended);
    requestMeasure();
  }
  if (mobileMenu && window.MutationObserver) {
    var mobileObserver = new MutationObserver(syncMobileMenu);
    mobileObserver.observe(mobileMenu, { attributes: true, attributeFilter: ['hidden'] });
  }

  function syncForcedColors() {
    var forced = !!(forcedColors && forcedColors.matches);
    handle.hidden = forced;
    root.classList.toggle('has-page-scroll-handle', !forced);
    if (!forced) requestMeasure();
  }
  if (forcedColors) {
    if (forcedColors.addEventListener) forcedColors.addEventListener('change', syncForcedColors);
    else if (forcedColors.addListener) forcedColors.addListener(syncForcedColors);
  }

  syncMobileMenu();
  syncForcedColors();
  requestMeasure();
})();
